"use client";

import Image from "next/image";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface TechnicalCertificate {
  description: string;
  src: string;
}

interface TechnicalFeature {
  icon: string;
  title: string;
  description: string;
}

const TechnicalSection = () => {
  const { t } = useTranslation();
  const certificates = t("TECHNICAL_SECTION_CERTIFICATES", {
    returnObjects: true,
  }) as TechnicalCertificate[];
  const features = t("TECHNICAL_SECTION_FEATURES", {
    returnObjects: true,
  }) as TechnicalFeature[];

  return (
    <SectionContainer
      id="technical-section"
      className="section-shell bg-white text-gray-900"
    >
      <AnimatedContent className="section-inner section-stack items-center">
        <div className="section-head">
          <h2 className="section-title">{t("TECHNICAL_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 section-lead whitespace-pre-wrap">
            {t("TECHNICAL_SECTION_SUB_TITLE")}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-center gap-10 w-full">
          <div className="relative w-full lg:w-2/5 aspect-square rounded-2xl overflow-hidden shadow-lg">
            <Image
              src="https://zipshow-static-web.s3.ap-northeast-2.amazonaws.com/homepage/color-to-life-biz/technical_image.png"
              alt={t("TECHNICAL_SECTION_MAIN_IMAGE_ALT")}
              fill
              quality={95}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              className="object-cover"
            />
          </div>

          <div className="w-full lg:w-3/5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {certificates.map((item, index) => (
              <div key={index} className="flex flex-col gap-3">
                <div className="relative aspect-[3/4] rounded-md overflow-hidden bg-white shadow-md">
                  <Image
                    src={item.src}
                    alt={item.description}
                    fill
                    quality={90}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 33vw, 20vw"
                    className="object-contain"
                  />
                </div>
                <p className="text-center metric-label px-2 whitespace-pre-wrap">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 w-full">
          {features.map((item) => (
            <div
              key={item.title}
              className="flex flex-col items-start sm:items-center lg:items-start gap-4 p-6 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors shadow-sm"
            >
              <div className="relative w-10 h-10">
                <Image
                  src={item.icon}
                  alt={item.title}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className="object-contain"
                />
              </div>
              <h4 className="font-semibold text-[clamp(1rem,1.8vw,1.25rem)]">
                {item.title}
              </h4>
              <p className="caption text-gray-600">{item.description}</p>
            </div>
          ))}
        </div>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default TechnicalSection;
